(() => {
  'use strict';

  if (customElements.get('c2b-slides-banner')) {
    return;
  }

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class C2BSlidesBanner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.track = this.querySelector('[data-c2b-slides-track]');
      this.slides = Array.from(this.querySelectorAll('[data-c2b-slide]'));
      this.prevButton = this.querySelector('[data-c2b-slide-prev]');
      this.nextButton = this.querySelector('[data-c2b-slide-next]');
      this.dots = Array.from(this.querySelectorAll('[data-c2b-slide-dot]'));
      this.dotHandlers = new Map();

      this.currentIndex = 0;
      this.timer = null;
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.interval = this.getSafeInterval(this.dataset.interval);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;

      if (!this.track || this.slides.length <= 0) {
        return;
      }

      this.bindEvents();
      this.goTo(0, false);

      if (this.autoplay && this.slides.length > 1 && !this.reducedMotion) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.unbindEvents();
      this.initialized = false;
    }

    getSafeInterval(value) {
      const parsed = Number.parseInt(value, 10);

      if (Number.isNaN(parsed)) {
        return 5000;
      }

      return Math.min(Math.max(parsed, 3000), 9000);
    }

    bindEvents() {
      this.handlePrev = this.handlePrev || (() => this.prev());
      this.handleNext = this.handleNext || (() => this.next());
      this.handleKeydown = this.handleKeydown || ((event) => this.onKeydown(event));
      this.handleMouseEnter = this.handleMouseEnter || (() => this.stopAutoplay());
      this.handleMouseLeave = this.handleMouseLeave || (() => this.startAutoplay());
      this.handleFocusIn = this.handleFocusIn || (() => this.stopAutoplay());
      this.handleFocusOut = this.handleFocusOut || ((event) => this.onFocusOut(event));
      this.handleReducedMotionChange = this.handleReducedMotionChange || ((event) => this.onReducedMotionChange(event));

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrev);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNext);
      }

      this.dots.forEach((dot) => {
        const handler = () => {
          const index = Number.parseInt(dot.dataset.slideIndex, 10);

          if (!Number.isNaN(index)) {
            this.goTo(index, true);
          }
        };

        this.dotHandlers.set(dot, handler);
        dot.addEventListener('click', handler);
      });

      this.addEventListener('keydown', this.handleKeydown);

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
      }
    }

    unbindEvents() {
      if (this.prevButton && this.handlePrev) {
        this.prevButton.removeEventListener('click', this.handlePrev);
      }

      if (this.nextButton && this.handleNext) {
        this.nextButton.removeEventListener('click', this.handleNext);
      }

      this.dotHandlers.forEach((handler, dot) => {
        dot.removeEventListener('click', handler);
      });
      this.dotHandlers.clear();

      if (this.handleKeydown) {
        this.removeEventListener('keydown', this.handleKeydown);
      }

      if (this.reducedMotionQuery && this.reducedMotionQuery.removeEventListener && this.handleReducedMotionChange) {
        this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotionChange);
      }

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) {
        return;
      }

      this.startAutoplay();
    }

    onReducedMotionChange(event) {
      this.reducedMotion = event.matches;

      if (this.reducedMotion) {
        this.stopAutoplay();
        return;
      }

      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplay || this.slides.length <= 1 || this.reducedMotion || this.timer) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.next();
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    }

    prev() {
      const nextIndex = this.currentIndex - 1;

      if (nextIndex < 0) {
        this.goTo(this.loop ? this.slides.length - 1 : 0, true);
        return;
      }

      this.goTo(nextIndex, true);
    }

    next() {
      const nextIndex = this.currentIndex + 1;

      if (nextIndex >= this.slides.length) {
        this.goTo(this.loop ? 0 : this.slides.length - 1, true);
        return;
      }

      this.goTo(nextIndex, true);
    }

    goTo(index, resetAutoplay) {
      const safeIndex = Math.min(Math.max(index, 0), this.slides.length - 1);

      this.currentIndex = safeIndex;
      this.track.style.transform = `translate3d(-${safeIndex * 100}%, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === safeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        this.updateSlideTabIndex(slide, isActive);
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === safeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      if (this.prevButton) {
        this.prevButton.disabled = !this.loop && safeIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.loop && safeIndex === this.slides.length - 1;
      }

      if (resetAutoplay && this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateSlideTabIndex(slide, isActive) {
      const focusableElements = Array.from(slide.querySelectorAll(FOCUSABLE_SELECTOR));

      focusableElements.forEach((element) => {
        if (isActive) {
          if (element.dataset.c2bOriginalTabindex !== undefined) {
            const originalValue = element.dataset.c2bOriginalTabindex;

            if (originalValue === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalValue);
            }

            delete element.dataset.c2bOriginalTabindex;
          }

          return;
        }

        if (element.dataset.c2bOriginalTabindex === undefined) {
          element.dataset.c2bOriginalTabindex = element.getAttribute('tabindex') || '';
        }

        element.setAttribute('tabindex', '-1');
      });
    }
  }

  customElements.define('c2b-slides-banner', C2BSlidesBanner);
})();
